import { GridApi, GridOptions, GridReadyEvent, ManagedGridOptionKey } from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * ag-grid-react builds its grid from the props of the render that first attached the grid element's
 * ref, and from then on hands the grid only what changed between one render and the next. React
 * detaches that ref when a Suspense boundary above the grid hides its content (a sibling suspended
 * after the grid had mounted) and ag-grid-react destroys the grid; on reveal the ref comes back and
 * ag-grid-react builds a new grid from those first props again, while its diffing takes the latest
 * props as already applied. That grid shows the rows and the loading state the element mounted
 * with and reports to the callbacks of that first render, so a `rowDataUpdated` it fires publishes
 * the previous date's rows and the page's loader waits for a publish that never comes.
 *
 * The returned `onGridReady` brings whichever grid reports ready up to the latest props. A grid
 * built from them (every grid outside that path, and StrictMode's dev-only rebuild on mount) has
 * nothing to apply. `onGridReady` itself is always the caller's latest.
 *
 * The caller renders `AgGridReact` in the same render it first calls this hook, so the hook's first
 * `gridProps` are the ones ag-grid-react builds from. A wrapper that mounted the grid on a later
 * render would make the hook resend a few options the grid already holds: harmless, but wasted.
 */
export function useLatestPropsOnRebuiltGrid(
  gridProps: AgGridReactProps,
  onGridReady: AgGridReactProps['onGridReady']
): (event: GridReadyEvent) => void {
  // The props ag-grid-react builds every grid of this element from.
  const [builtFrom] = useState(gridProps);
  const latestRef = useRef({ gridProps, onGridReady });
  useLayoutEffect(() => {
    latestRef.current = { gridProps, onGridReady };
  });

  return useCallback(
    (event: GridReadyEvent) => {
      const behind = optionsBehindProps(event.api, builtFrom, latestRef.current.gridProps);
      if (Object.keys(behind).length > 0) {
        event.api.updateGridOptions(behind);
      }
      latestRef.current.onGridReady?.(event);
    },
    [builtFrom]
  );
}

/** Props of `AgGridReact` that are not grid options; ag-grid-react leaves them out of its own diff too. */
const NOT_GRID_OPTIONS = new Set([
  'gridOptions',
  'modules',
  'containerStyle',
  'className',
  'passGridApi',
  'componentWrappingElement',
  'setGridApi',
  'maxComponentCreationTimeMs',
  'children',
  'ref',
  'onGridReady',
]);

/**
 * Grid options ag-grid reads once, at creation: `updateGridOptions` only warns about them. Typed
 * against ag-grid's own split, so an upgrade that moves an option fails to compile here.
 */
const INITIAL_ONLY_OPTIONS: Record<Exclude<keyof GridOptions, ManagedGridOptionKey>, true> = {
  enableBrowserTooltips: true,
  tooltipTrigger: true,
  tooltipMouseTrack: true,
  tooltipShowMode: true,
  tooltipInteraction: true,
  defaultColGroupDef: true,
  suppressAutoSize: true,
  skipHeaderOnAutoSize: true,
  autoSizeStrategy: true,
  components: true,
  stopEditingWhenCellsLoseFocus: true,
  undoRedoCellEditing: true,
  undoRedoCellEditingLimit: true,
  excelStyles: true,
  cacheQuickFilter: true,
  customChartThemes: true,
  chartThemeOverrides: true,
  chartToolPanelsDef: true,
  loadingCellRendererSelector: true,
  localeText: true,
  keepDetailRows: true,
  keepDetailRowsCount: true,
  detailRowHeight: true,
  detailRowAutoHeight: true,
  tabIndex: true,
  valueCache: true,
  valueCacheNeverExpires: true,
  enableCellExpressions: true,
  suppressTouch: true,
  suppressBrowserResizeObserver: true,
  suppressPropertyNamesCheck: true,
  debug: true,
  dragAndDropImageComponent: true,
  overlayComponent: true,
  suppressOverlays: true,
  loadingOverlayComponent: true,
  suppressLoadingOverlay: true,
  noRowsOverlayComponent: true,
  paginateChildRows: true,
  pivotPanelShow: true,
  pivotSuppressAutoColumn: true,
  suppressExpandablePivotGroups: true,
  aggFuncs: true,
  allowShowChangeAfterFilter: true,
  ensureDomOrder: true,
  enableRtl: true,
  suppressColumnVirtualisation: true,
  suppressMaxRenderedRowRestriction: true,
  suppressRowVirtualisation: true,
  rowDragText: true,
  groupLockGroupColumns: true,
  suppressGroupRowsSticky: true,
  rowModelType: true,
  cacheOverflowSize: true,
  infiniteInitialRowCount: true,
  serverSideInitialRowCount: true,
  maxBlocksInCache: true,
  maxConcurrentDatasourceRequests: true,
  blockLoadDebounceMillis: true,
  serverSideOnlyRefreshFilteredGroups: true,
  serverSidePivotResultFieldSeparator: true,
  viewportRowModelPageSize: true,
  viewportRowModelBufferSize: true,
  debounceVerticalScrollbar: true,
  suppressAnimationFrame: true,
  suppressPreventDefaultOnMouseWheel: true,
  scrollbarWidth: true,
  icons: true,
  suppressRowTransform: true,
  suppressContentVisibilityAuto: true,
  enableContentVisibilityAuto: true,
  contentVisibilityAutoDelay: true,
  gridId: true,
  enableGroupEdit: true,
  initialState: true,
  processUnpinnedColumns: true,
  createChartContainer: true,
  getLocaleText: true,
  getRowId: true,
  reactiveCustomComponents: true,
  renderingMode: true,
  columnMenu: true,
  suppressSetFilterByDefault: true,
  getDataPath: true,
  enableCellSpan: true,
  enableFilterHandlers: true,
  filterHandlers: true,
};

/**
 * The grid options that changed since the props the grid was built from and that the grid still does
 * not hold. Empty for a grid built from the latest props, and for one that already took the changes.
 * Options read only at creation are left out: the grid would ignore them and warn.
 */
export function optionsBehindProps(
  api: GridApi,
  builtFrom: AgGridReactProps,
  latest: AgGridReactProps
): Partial<GridOptions> {
  const behind: Record<string, unknown> = {};
  for (const key of Object.keys(latest)) {
    if (NOT_GRID_OPTIONS.has(key) || key in INITIAL_ONLY_OPTIONS) {
      continue;
    }
    const value = latest[key as keyof AgGridReactProps];
    if (builtFrom[key as keyof AgGridReactProps] === value) {
      continue;
    }
    if (api.getGridOption(key as keyof GridOptions) === value) {
      continue;
    }
    behind[key] = value;
  }
  return behind as Partial<GridOptions>;
}
